import React, {memo, useCallback, useEffect, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Language} from "../../../types/Responses/LanguagesResponse";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import DropdownInput from "../../../components/DropdownInput";
import {useUserContext} from "../../../types/contexts/UserContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {setSidebarTitle} from "../../../reducers/sidebar";

const LanguageForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const toast = useToastContext();
    const navigate = useNavigate();
    const system = useSystemContext();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [name, setName] = useState<string>();
    const [code, setCode] = useState<string>();
    const [status, setStatus] = useState<number>(1);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);


    const changeName = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setName(e.currentTarget.value)
        }
    }

    const changeCode = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setCode(e.currentTarget.value)
        }
    }

    const handleSuccessResponse = (response: AxiosResponse, message: JSX.Element | string) => {
        const language = response.data.data as Language
        if (language) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/languages');
        }
    }

    const callback = (b: boolean) => {
        setValidated(true);
        setInProgress(false);
        if (b) {
            const request = {name, status, code};
            if (id) {
                API.updateLanguage(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.languages.updated.title', {name}))).catch(e => ErrorHandler(e, `put${APILink.GET_LANGUAGE.replace('$language', id.toString())}`, toast, handleError));
            } else {
                API.createLanguage(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/languages'));
                    handleSuccessResponse(response, t('admin.languages.created.title', {name}));
                }).catch(e => ErrorHandler(e, `post${APILink.GET_LANGUAGES}`, toast, handleError));
            }

        }
    }

    const submit = () => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {key: 'name', label: t('admin.languages.table.name'), type: InputType.text, regex: '^.{3,}\\b$'},
                    {key: 'code', label: t('admin.languages.table.code'), type: InputType.text, regex: '^.{2,2}$'},
                ],
                introducedData: {name, code},
                callback: callback
            }
        }));
    }
    const getSingleLanguage = useCallback(() => {
        API.getLanguage(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const languageResponse = response.data.data as Language
            setName(languageResponse.name);
            setCode(languageResponse.code);
            if (system && system.system) {
                setStatus(system.system.statuses.find(s => s.name === languageResponse.status)?.id ?? 0)
            }
            setFormIsReady(true);
            setTitle(t('admin.buttons.update') + " " + languageResponse.name);
            dispatch(setSidebarTitle(t('admin.languages.update.title', {name: languageResponse.name})));
        }).catch(e => {
            ErrorHandler(e, `get${APILink.GET_LANGUAGE.replace('$language', id?.toString() ?? '')}`, toast, handleError, setIsForbidden)
            setFormIsReady(true);
        });
    }, [setName, setCode, setStatus, setFormIsReady, system, toast, handleError, id]);

    const onStatusChanged = (s: string | number) => {
        setStatus(typeof s === 'string' ? parseInt(s) : s);
    }

    useEffect(() => {
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                if (id) {
                    getSingleLanguage();
                } else {
                    setTitle(t('admin.buttons.create'));
                    dispatch(setSidebarTitle(t('admin.languages.create.title')));
                    setFormIsReady(true);
                    if (user && !user.user.permissions.includes('create language')) {
                        setIsForbidden(true);
                    }
                }
            }).catch();
        }
        dispatch(clear())
    }, [id, dispatch, getSingleLanguage, t]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden && <>
                            <div className="max-w-md">
                                <Input success={!hasError('name', errors) && validated}
                                       error={validated && hasError('name', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.languages.table.name')} type={'text'}
                                       onChange={changeName}
                                       value={name}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('name', errors) &&
                                    <label key={'error-name'}
                                           className="mt-1 text-red-500 text-xs block">{getError('name', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <Input success={!hasError('code', errors) && validated}
                                       error={validated && hasError('code', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.languages.table.code')} type={'text'}
                                       onChange={changeCode}
                                       value={code}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('code', errors) &&
                                    <label key={'error-code'}
                                           className="mt-1 text-red-500 text-xs block">{getError('code', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <DropdownInput name={t('admin.languages.table.status')} selected={status}
                                               onChange={onStatusChanged}
                                               list={system?.system?.statuses.map((s) => {
                                                   return {id: s.id, name: t(s.name)}
                                               }) ?? []}/>
                            </div>
                            <div className="mt-12">
                                <Button onClick={submit} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </>}
                        <Forbidden isForbidden={isForbidden}/>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    )
        ;
}
export default memo(LanguageForm);
